import React, { Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Features } from "./components/Features";
import { ScrollToHash } from "./components/ScrollToHash";
import {
  absoluteUrl,
  setCanonicalUrl,
  setDocumentTitle,
  setMetaDescription,
  setOpenGraph,
  setTwitterCard,
} from "./lib/seo";
import {
  injectHomepageStructuredData,
  removeHomepageStructuredData,
  injectGuideStructuredData,
  removeGuideStructuredData,
} from "./lib/structuredData";
import { getGuideBySlug, guideArticles } from "./lib/guideData";

// Lazy-load sub-pages to reduce initial bundle size
const PrivacyPolicy = React.lazy(() =>
  import("./pages/PrivacyPolicy").then((m) => ({ default: m.PrivacyPolicy }))
);
const TermsOfService = React.lazy(() =>
  import("./pages/TermsOfService").then((m) => ({ default: m.TermsOfService }))
);
const Support = React.lazy(() =>
  import("./pages/Support").then((m) => ({ default: m.Support }))
);
const NotFound = React.lazy(() =>
  import("./pages/NotFound").then((m) => ({ default: m.NotFound }))
);
const GuideIndex = React.lazy(() =>
  import("./pages/GuideIndex").then((m) => ({ default: m.GuideIndex }))
);
const GuidePage = React.lazy(() =>
  import("./pages/GuidePage").then((m) => ({ default: m.GuidePage }))
);


function App() {
  const location = useLocation();

  React.useEffect(() => {
    const pathname = location.pathname || "/";

    const defaultTitle = "knooth — Your screen. Perfectly recorded.";
    const defaultDescription =
      "knooth is a macOS screen recording app. Record your screen, highlight key moments, and export polished videos — with built-in editing, AI captions, and timeline animations.";

    const page = (() => {
      if (pathname === "/privacy-policy") {
        return {
          title: "Privacy Policy — knooth",
          description: "How knooth handles your data and privacy.",
        };
      }
      if (pathname === "/terms-of-service") {
        return {
          title: "Terms of Service — knooth",
          description: "The terms that govern use of the knooth app.",
        };
      }
      if (pathname === "/support") {
        return {
          title: "Contact support — knooth",
          description:
            "Get in touch with the knooth team. Send us a message, report an issue, or share feedback — attachments welcome.",
        };
      }
      if (pathname === "/guide") {
        return {
          title: "Guide — knooth",
          description: "The complete guide to recording, editing, and exporting with knooth on Mac.",
        };
      }
      if (pathname.startsWith("/guide/")) {
        const guideSlug = pathname.replace("/guide/", "");
        const guide = getGuideBySlug(guideSlug);
        if (guide) {
          return {
            title: `${guide.title} — knooth Guide`,
            description: guide.description,
          };
        }
      }
      return { title: defaultTitle, description: defaultDescription };
    })();

    const url = absoluteUrl(pathname);

    setDocumentTitle(page.title);
    setMetaDescription(page.description);
    setCanonicalUrl(url);

    setOpenGraph({
      title: page.title,
      description: page.description,
      url,
    });

    setTwitterCard({
      title: page.title,
      description: page.description,
    });

    // Structured data: homepage, guide pages, or cleanup
    if (pathname === "/") {
      injectHomepageStructuredData();
      removeGuideStructuredData();
    } else if (pathname.startsWith("/guide/")) {
      const guideSlug = pathname.replace("/guide/", "");
      const guide = getGuideBySlug(guideSlug);
      if (guide) {
        injectGuideStructuredData({
          title: guide.title,
          description: guide.description,
          slug: guide.slug,
        });
      }
      removeHomepageStructuredData();
    } else {
      removeHomepageStructuredData();
      removeGuideStructuredData();
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FBFBFD]">
      <Navbar />
      <ScrollToHash />

      <main>
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Hero />

                  {/* 80px spacing between hero and features */}
                  <div className="h-[150px] md:h-[200px]" />

                  <Features />
                </>
              }
            />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/support" element={<Support />} />
            <Route path="/guide" element={<GuideIndex />} />
            <Route path="/guide/:slug" element={<GuidePage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>


    </div>
  );
}

export default App;
